// Product photos live in the public Supabase Storage bucket "product-images".
// products.images holds their public URLs (at most 6, first = catalogue photo).
//
// Page images (hero, about, collection heroes) are still kept in this
// browser as base64 under aml_img_page_* until the site_content step.
// Old product photos may also still sit here under aml_img_product_*;
// moveLocalProductPhotosOnline() uploads them and swaps them for URLs.

import { supabase } from './supabaseClient';

export var BUCKET = 'product-images';
export var MAX_PRODUCT_IMAGES = 6;
export var MAX_IMAGE_BYTES = 5 * 1024 * 1024;

var PREFIX = 'aml_img_';
var PUBLIC_MARKER = '/storage/v1/object/public/' + BUCKET + '/';

// ---------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------

export function safeFileName(name) {
  var base = String(name || 'photo').toLowerCase().split(/[\\/]/).pop();
  var dot = base.lastIndexOf('.');
  var ext = dot > 0 ? base.slice(dot + 1).replace(/[^a-z0-9]/g, '').slice(0, 5) : '';
  var stem = (dot > 0 ? base.slice(0, dot) : base)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'photo';
  return ext ? stem + '.' + ext : stem;
}

// Never hand out the same timestamp twice, so photos picked together with
// the same file name can't overwrite each other.
var lastStamp = 0;
function uniqueStamp() {
  lastStamp = Math.max(Date.now(), lastStamp + 1);
  return lastStamp;
}

export function productImagePath(productId, fileName, now) {
  var folder = productId ? String(productId).replace(/[^A-Za-z0-9_-]/g, '') || 'new' : 'new';
  return 'products/' + folder + '/' + (now || uniqueStamp()) + '-' + safeFileName(fileName);
}

// Returns the path inside the bucket for one of our public URLs, or null.
export function pathFromPublicUrl(url) {
  if (typeof url !== 'string') return null;
  var at = url.indexOf(PUBLIC_MARKER);
  if (at < 0) return null;
  var path = url.slice(at + PUBLIC_MARKER.length).split('?')[0].split('#')[0];
  try { path = decodeURIComponent(path); } catch (e) { /* keep as is */ }
  return path || null;
}

// Must match allowed_mime_types in 20261103000000_product_images.sql
export var ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif', 'image/heic', 'image/heif'];

export function checkImageFile(file) {
  if (!file) return 'No photo was chosen.';
  if (!file.type || ALLOWED_IMAGE_TYPES.indexOf(file.type) < 0) {
    return 'That file is not a photo. Please choose a JPG, PNG or WebP image.';
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return 'That photo is too large. Please use a photo under 5 MB.';
  }
  return null;
}

function friendlyStorageError(error) {
  var msg = String((error && (error.message || error.error)) || '').toLowerCase();
  var status = String((error && (error.statusCode || error.status)) || '');
  if (msg.indexOf('row-level security') >= 0 || msg.indexOf('unauthorized') >= 0 || status === '403' || status === '401') {
    return 'Only a signed-in admin can change photos. Please sign in again and retry.';
  }
  if (msg.indexOf('bucket not found') >= 0) {
    return 'The photo storage is not set up yet (the product-images bucket is missing).';
  }
  if (msg.indexOf('too large') >= 0 || msg.indexOf('maximum allowed size') >= 0 || status === '413') {
    return 'That photo is too large. Please use a photo under 5 MB.';
  }
  if (msg.indexOf('mime') >= 0 || msg.indexOf('invalid_mime_type') >= 0) {
    return 'That file type is not allowed. Please choose a JPG, PNG or WebP image.';
  }
  return 'Could not upload the photo. Please check your internet connection and try again.';
}

export async function uploadProductImage(file, productId) {
  var problem = checkImageFile(file);
  if (problem) return { url: null, error: problem };
  if (!supabase) return { url: null, error: 'Not connected to the database. The photo was not uploaded.' };

  var path = productImagePath(productId, file.name);
  try {
    var result = await supabase.storage.from(BUCKET).upload(path, file, {
      contentType: file.type,
      cacheControl: '31536000',
      upsert: false,
    });
    if (result.error) {
      console.error('Photo upload failed:', result.error);
      return { url: null, error: friendlyStorageError(result.error) };
    }
    var pub = supabase.storage.from(BUCKET).getPublicUrl(result.data.path || path);
    return { url: pub.data.publicUrl, error: null };
  } catch (e) {
    console.error('Photo upload failed:', e);
    return { url: null, error: friendlyStorageError(e) };
  }
}

export async function deleteProductImage(url) {
  var path = pathFromPublicUrl(url);
  if (!path) return { error: null }; // not one of ours (e.g. an old data URL): nothing to delete
  if (!supabase) return { error: 'Not connected to the database. The photo was not deleted.' };
  try {
    var result = await supabase.storage.from(BUCKET).remove([path]);
    if (result.error) {
      console.error('Photo delete failed:', result.error);
      return { error: 'Could not delete the photo. Please check your internet connection and try again.' };
    }
    return { error: null };
  } catch (e) {
    console.error('Photo delete failed:', e);
    return { error: 'Could not delete the photo. Please check your internet connection and try again.' };
  }
}

// ---------------------------------------------------------------------
// Old product photos saved in this browser (before step 6)
// ---------------------------------------------------------------------

export function getLocalProductImages(productId) {
  var images = [];
  for (var i = 0; i < MAX_PRODUCT_IMAGES; i++) {
    var img = getImage('product_' + productId + '_' + i);
    if (img) images.push(img);
  }
  return images;
}

export function removeLocalProductImages(productId) {
  for (var i = 0; i < MAX_PRODUCT_IMAGES; i++) removeImage('product_' + productId + '_' + i);
}

// Product ids that still have photos in this browser ("new" = never saved).
export function listLocalProductIds() {
  var ids = {};
  var re = /^aml_img_product_(.+)_(\d+)$/;
  for (var i = 0; i < localStorage.length; i++) {
    var m = re.exec(localStorage.key(i) || '');
    if (m) ids[m[1]] = true;
  }
  return Object.keys(ids);
}

export function dataUrlToFile(dataUrl, name) {
  var m = /^data:([^;,]+)(;base64)?,(.*)$/.exec(dataUrl || '');
  if (!m) return null;
  var type = m[1];
  var bytes;
  if (m[2]) {
    var bin = atob(m[3]);
    bytes = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  } else {
    bytes = new TextEncoder().encode(decodeURIComponent(m[3]));
  }
  var ext = (type.split('/')[1] || 'jpg').replace('jpeg', 'jpg').replace(/[^a-z0-9]/g, '');
  return new File([bytes], (name || 'photo') + '.' + ext, { type: type });
}

// Uploads every old photo in this browser for the given products, saves the
// product with the new URLs (existing URLs first, then the old photos, 6 max)
// and clears each old copy only once it is uploaded AND the product is saved.
// Anything not moved (no room, unreadable, refused, offline) stays in this
// browser, so nothing is ever lost and the button can be tried again.
// saveProduct is ProductsContext.saveProduct. Returns a summary for a toast.
export async function moveLocalProductPhotosOnline(products, saveProduct) {
  var summary = { moved: 0, products: 0, skipped: 0, errors: [] };
  var ids = listLocalProductIds();
  var byId = {};
  (products || []).forEach(function(x) { byId[String(x.id)] = x; });

  for (var p = 0; p < ids.length; p++) {
    var id = ids[p];
    var product = byId[id];
    var keys = [];
    for (var k = 0; k < MAX_PRODUCT_IMAGES; k++) {
      var key = 'product_' + id + '_' + k;
      if (getImage(key)) keys.push(key);
    }
    if (!product) {
      // Photos for a product that was never saved, or has been deleted.
      summary.skipped += keys.length;
      continue;
    }
    var urls = (product.images || []).filter(function(u) { return typeof u === 'string' && u.indexOf('data:') !== 0; });
    var room = MAX_PRODUCT_IMAGES - urls.length;
    var uploaded = [];
    var movedKeys = [];
    for (var i = 0; i < keys.length; i++) {
      if (uploaded.length >= room) { summary.skipped += keys.length - i; break; }
      var file = dataUrlToFile(getImage(keys[i]), safeFileName(product.slug || product.name || 'photo') + '-' + (i + 1));
      if (!file) { summary.skipped++; continue; }
      var res = await uploadProductImage(file, product.id);
      if (res.error) {
        summary.skipped++;
        if (summary.errors.indexOf(product.name + ': ' + res.error) < 0) summary.errors.push(product.name + ': ' + res.error);
        continue;
      }
      uploaded.push(res.url);
      movedKeys.push(keys[i]);
    }
    if (uploaded.length === 0) continue;

    var saved = await saveProduct(Object.assign({}, product, { images: urls.concat(uploaded) }));
    if (saved.error) {
      summary.errors.push(product.name + ': ' + saved.error);
      summary.skipped += uploaded.length;
      for (var d = 0; d < uploaded.length; d++) await deleteProductImage(uploaded[d]);
      continue;
    }
    movedKeys.forEach(removeImage);
    summary.moved += uploaded.length;
    summary.products++;
  }
  return summary;
}

// ---------------------------------------------------------------------
// Page images (still in this browser; moved in a later step)
// ---------------------------------------------------------------------

export function getImage(key) {
  return localStorage.getItem(PREFIX + key) || null;
}

export function setImage(key, dataUrl) {
  try {
    localStorage.setItem(PREFIX + key, dataUrl);
    return true;
  } catch (e) {
    if (e.name === 'QuotaExceededError') {
      alert('Storage full. Try using smaller images or removing unused ones.');
    }
    return false;
  }
}

export function removeImage(key) {
  localStorage.removeItem(PREFIX + key);
}

export function getPageImage(pageKey) {
  return getImage('page_' + pageKey);
}

export function setPageImage(pageKey, dataUrl) {
  return setImage('page_' + pageKey, dataUrl);
}

export function removePageImage(pageKey) {
  removeImage('page_' + pageKey);
}
